import React, { useEffect, useState } from "react";

//material ui imports
import {
  Grid,
  TextField,
  Typography,
  Divider,
  Button,
  Stack,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import SaveIcon from "@mui/icons-material/Save";
import { useTheme } from "@mui/material/styles";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";

//third party imports
import * as Yup from "yup";
import { useFormik } from "formik";
import useAxiosAuth from "../../../../hooks/useAxiosAuth";

const EmployerSpecializationForm = (props) => {
  //Constants
  const { onSave, onCancel, selectedEmployerSpecialization } = props;
  const axiosAuth = useAxiosAuth();
  const theme = useTheme();

  //States
  const [loading, setLoading] = useState(false);
  const [specializations, setSpecializations] = useState([]);
  const [subSpecializations, setSubSpecializations] = useState([]);
  const pageSize = 1000;

  const formik = useFormik({
    initialValues: {
      id: selectedEmployerSpecialization.id,
      employerId: selectedEmployerSpecialization.employerId,
      employerSpecializationMasterId:
        selectedEmployerSpecialization.employerSpecializationMasterId || 0,
      employerSubSpecializationMasterId:
        selectedEmployerSpecialization.employerSubSpecializationMasterId || 0,
      specializationName:
        selectedEmployerSpecialization.specializationName || "",
      subSpecializationName:
        selectedEmployerSpecialization.subSpecializationName || "",
    },
    validationSchema: Yup.object({
      specializationName: Yup.string().max(
        100,
        "Must be 100 characters or less"
      ),
      subSpecializationName: Yup.string().max(
        100,
        "Must be 100 characters or less"
      ),
    }),

    onSubmit: (values) => {
      setLoading(true);
      if (selectedEmployerSpecialization.id === 0) {
        axiosAuth
          .post("/api/EmployerSpecializations", values)
          .then((response) => {
            if (response.status === 200) {
              onSave();
            } else {
              setLoading(false);
            }
          })
          .catch((error) => {
            console.log(error);
            setLoading(false);
          });
      } else {
        axiosAuth
          .put("/api/EmployerSpecializations", values)
          .then((response) => {
            if (response.status === 200) {
              onSave();
            } else {
              setLoading(false);
            }
          })
          .catch((error) => {
            console.log(error);
            setLoading(false);
          });
      }
    },
  });

  // Load specializations on component mount
  useEffect(() => {
    getSpecializations();
  }, []);

  useEffect(() => {
    if (formik.values.employerSpecializationMasterId) {
      getSubSpecializations(formik.values.employerSpecializationMasterId);
    } else {
      setSubSpecializations([]);
    }
  }, [formik.values.employerSpecializationMasterId]);

  //Services
  const getSpecializations = () => {
    axiosAuth
      .get("/api/EmployerSpecialzationMasters/list?skip=0&take=" + pageSize)
      .then((response) => {
        setSpecializations(response.data);
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const getSubSpecializations = (specializationId) => {
    axiosAuth
      .get(
        "/api/EmployerSubSpecializationMasters/by-employer-specialization?employerSpecializationId=" +
          specializationId
      )
      .then((response) => {
        setSubSpecializations(response.data);
      })
      .catch((error) => {
        console.log(error);
      });
  };

  // Handle specialization selection
  const handleSpecializationChange = (event) => {
    const specializationId = event.target.value;

    // Update formik values
    formik.setFieldValue("employerSpecializationMasterId", specializationId);

    // Find the selected specialization name
    const selectedSpec = specializations.find(
      (spec) => spec.id === specializationId
    );
    if (selectedSpec) {
      formik.setFieldValue("specializationName", selectedSpec.name);
    }

    // Reset sub-specialization field when specialization changes
    formik.setFieldValue("employerSubSpecializationMasterId", 0);
    formik.setFieldValue("subSpecializationName", "");
  };

  // Handle sub-specialization selection
  const handleSubSpecializationChange = (event) => {
    const subSpecializationId = event.target.value;

    // Update formik values
    formik.setFieldValue(
      "employerSubSpecializationMasterId",
      subSpecializationId
    );

    // Find the selected sub-specialization name
    const selectedSubSpec = subSpecializations.find(
      (subSpec) => subSpec.id === subSpecializationId
    );
    if (selectedSubSpec) {
      formik.setFieldValue("subSpecializationName", selectedSubSpec.name);
    }
  };

  return (
    <>
      <Grid container>
        {selectedEmployerSpecialization.id === 0 ? (
          <Stack direction="row" sx={{ marginX: "auto", alignItems: "center" }}>
            <AccountBalanceIcon />

            <Typography
              color={theme.palette.primary.dark}
              variant="h6"
              textAlign="center"
              sx={{ marginLeft: 2 }}
            >
              Add New Specialization
            </Typography>
          </Stack>
        ) : (
          <Stack direction="row" sx={{ marginX: "auto", alignItems: "center" }}>
            <AccountBalanceIcon />
            <Typography
              color={theme.palette.primary.dark}
              variant="h6"
              textAlign="center"
              sx={{ marginLeft: 2 }}
            >
              Edit Specialization Details
            </Typography>
          </Stack>
        )}
      </Grid>
      <Divider sx={{ marginY: 2 }} />
      <Grid container spacing={3}>
        <Grid item xl={12} lg={12} md={12} sm={12}>
          <form onSubmit={formik.handleSubmit}>
            <FormControl sx={{ marginBottom: 3 }} fullWidth>
              <InputLabel>Select Specialization</InputLabel>
              <Select
                name="employerSpecializationMasterId"
                label="Select Specialization"
                onChange={handleSpecializationChange}
                value={formik.values.employerSpecializationMasterId}
                error={
                  formik.touched.employerSpecializationMasterId &&
                  Boolean(formik.errors.employerSpecializationMasterId)
                }
              >
                {specializations.map((option) => (
                  <MenuItem key={option.id} value={option.id}>
                    {option.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {/* <TextField
              name="specializationName"
              label="Specialization Name"
              onChange={formik.handleChange}
              value={formik.values.specializationName}
              fullWidth
              error={
                formik.touched.specializationName &&
                Boolean(formik.errors.specializationName)
              }
              helperText={
                formik.touched.specializationName &&
                formik.errors.specializationName
              }
              sx={{ marginBottom: 3 }}
            /> */}
            <FormControl sx={{ marginBottom: 3 }} fullWidth>
              <InputLabel>Select Sub Specialization/Products</InputLabel>
              <Select
                name="employerSubSpecializationMasterId"
                label="Select Sub Specialization/Products"
                onChange={handleSubSpecializationChange}
                value={formik.values.employerSubSpecializationMasterId}
                error={
                  formik.touched.employerSubSpecializationMasterId &&
                  Boolean(formik.errors.employerSubSpecializationMasterId)
                }
                disabled={!formik.values.employerSpecializationMasterId}
              >
                {subSpecializations.map((option) => (
                  <MenuItem key={option.id} value={option.id}>
                    {option.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {/* <TextField
              name="subSpecializationName"
              label="Sub Specialization Name"
              onChange={formik.handleChange}
              value={formik.values.subSpecializationName}
              fullWidth
              error={
                formik.touched.subSpecializationName &&
                Boolean(formik.errors.subSpecializationName)
              }
              helperText={
                formik.touched.subSpecializationName &&
                formik.errors.subSpecializationName
              }
              sx={{ marginBottom: 3 }}
            /> */}
            <Divider sx={{ marginBottom: 2 }} />
            <Grid>
              <LoadingButton
                variant="contained"
                type="submit"
                loading={loading}
                loadingPosition="end"
                endIcon={<SaveIcon />}
              >
                Submit
              </LoadingButton>
              <Button
                variant="outlined"
                onClick={onCancel}
                sx={{ marginLeft: 2 }}
              >
                Cancel
              </Button>
            </Grid>
          </form>
        </Grid>
      </Grid>
    </>
  );
};

export default EmployerSpecializationForm;
